import { parseAsync } from 'json2csv';
import _ from 'lodash';

import { getProject, getForms } from './App';

const CONTAINER_TYPES = ['project', 'subject', 'session', 'acquisition'];

const getDataViews = async (extension, projectId) => {
  return (
    (
      await extension.executeDataView(projectId, {
        label: 'DataView',
        description: 'DataView',
        columns: CONTAINER_TYPES.flatMap((containerType) => [
          { src: `${containerType}.id` },
          { src: `${containerType}.label` },
          { src: `${containerType}.info` },
        ]),
      })
    ).data || []
  );
};

const mergeData = (formDefs, data) => {
  return data.map((dataViewRow) => {
    const row = Object.assign({}, dataViewRow);

    CONTAINER_TYPES.forEach((containerType) => {
      const selector = `${containerType}.info`;
      const matchingFormDefs = _.keyBy(
        formDefs[containerType] || [],
        (v) => v.key
      );
      const info = dataViewRow[selector];
      row[selector] = _.merge({}, info, matchingFormDefs);
    });
    return row;
  });
};

/**
 * Used to convert the `data` to a flattened version by converting each row's
 * `containerType.info` objects into the columns needed.
 * @param {array} data - array of data

 * @param {string} exportType - exportType 'raw' or 'label'.
 * @returns {array} flattenedData
 */

const flattenData = (data, exportType) => {
  const typedData = Object.assign([], data);

  const flattenedData = typedData.map((row) => {
    const columns = {};
    CONTAINER_TYPES.forEach((containerType) => {
      const selector = `${containerType}.info`;
      const containerInfo = row[selector];

      for (const [key, form] of Object.entries(containerInfo)) {
        const components = form.components || [];
        columns[key] = components.map((field) => {
          return {
            label:
              exportType === 'label'
                ? field.label
                : `${containerType}.${key}.${field.key}`,
            fieldKey: `${containerType}.${key}.${field.key}`,
            value: containerInfo[key]?.data?.[field.key] || null,
          };
        });
      }
      row['columns'] = columns;
    });
    return row;
  });

  return flattenedData;
};

const valueRenderer = (fieldKey, row, field) => {
  return row.find((v) => v.fieldKey === fieldKey)?.value || field.default;
};

const getFields = (data) => {
  return _.uniqBy(
    _.flatten(
      data.map((row) => {
        return _.flatten(_.values(row.columns)).map(({ label, fieldKey }) => ({
          label,
          fieldKey,
          value: valueRenderer.bind(this, fieldKey),
          default: '-',
        }));
      })
    ),
    'fieldKey'
  );
};

const exportCsv = async ({ extension, exportType }) => {
  const project = await getProject(extension);
  const formDefs = await getForms(extension, project._id);
  const dataViews = await getDataViews(extension, project._id);
  const mergedData = mergeData(formDefs, dataViews);
  const flattenedData = flattenData(mergedData, exportType);
  const data = flattenedData.map((row) => _.flatten(_.values(row.columns)));
  const fields = getFields(flattenedData);
  const opts = { fields };
  const transformOpts = { highWaterMark: 8192 };
  const csv = await parseAsync(
    data,
    opts,
    transformOpts
  );

  return csv;
};

export { exportCsv };
